import { Link } from 'react-router-dom'
import { useVocabularyPack } from '../reference/queries.js'
import { AsOfLine, HairlineRule, Skeleton } from '../ui/index.js'
import styles from './RulesPage.module.css'

/**
 * THE RULES SCREEN — read-only, and every word on it is served.
 *
 * There is no string literal here matching a predicate id, a category or a
 * fact-type name, and that is the point rather than a style preference: the
 * failure this prevents is a predicate renamed in `QuotingPolicy.cs` while the
 * console keeps rendering the old word, with nothing breaking to say so.
 *
 * ── READ-ONLY, AND NOT BECAUSE IT IS UNFINISHED ───────────────────────────
 * No editor, no publish flow, no replay-diff. Those need a rule engine that
 * deliberately does not exist yet (ROADMAP E10/T4). A screen that let an
 * operator author a rule the runtime cannot evaluate would be worse than no
 * screen: it would take a decision and drop it.
 *
 * ── ONE RULE, RENDERED HONESTLY ───────────────────────────────────────────
 * The pack currently declares exactly one predicate. An earlier brief listed
 * eight; seven of them were aspirational. This renders whatever arrives, so
 * one row is not a special case and eight would need no change here.
 */
export function RulesPage() {
  const pack = useVocabularyPack()

  if (pack.isPending) {
    return (
      <main className={styles['page']}>
        <Skeleton />
      </main>
    )
  }

  if (pack.isError) {
    return (
      <main className={styles['page']}>
        <p role="alert" className={styles['error']}>
          {pack.error.message}
        </p>
      </main>
    )
  }

  const { predicates, authoringUIHints, factTypes, knownLimitations, completeness } = pack.data
  const entries = Object.entries(predicates)

  // Grouped by the category each predicate DECLARES, not by a grouping this
  // screen invents. `authoringUIHints.categories` gives the order; anything
  // declaring a category outside that list still renders, under its own name.
  const declared = authoringUIHints.categories
  const categories = [
    ...declared,
    ...[...new Set(entries.map(([, p]) => p.category))].filter((c) => !declared.includes(c)),
  ]

  return (
    <main className={styles['page']}>
      <div className={styles['header']}>
        <Link to="/">← Home</Link>
        <AsOfLine
          asOf={new Date(pack.dataUpdatedAt).toISOString()}
          onRefresh={() => {
            void pack.refetch()
          }}
        />
      </div>

      <h1 className={styles['title']}>Rules</h1>
      <p className={styles['lede']}>
        The policy this workspace is evaluated against, as the platform publishes it. Read-only —
        authoring needs a rule engine that does not exist yet.
      </p>

      <HairlineRule strong />

      {categories.map((category) => {
        const inCategory = entries.filter(([, predicate]) => predicate.category === category)
        if (inCategory.length === 0) return null

        return (
          <section key={category} className={styles['category']}>
            <h2 className={styles['categoryTitle']}>{category}</h2>
            {inCategory.map(([id, predicate]) => (
              <article key={id} className={styles['rule']}>
                <h3 className={styles['ruleLabel']}>{predicate.label}</h3>
                <p className={styles['logic']}>{predicate.logic_description}</p>
                <dl className={styles['meta']}>
                  <div>
                    <dt>Identifier</dt>
                    <dd className={styles['mono']}>{id}</dd>
                  </div>
                  <div>
                    <dt>Evaluated by</dt>
                    <dd className={styles['mono']}>
                      {predicate.evaluator.type} · {predicate.evaluator.reference}
                    </dd>
                  </div>
                  {predicate.inputs.length > 0 && (
                    <div>
                      <dt>Inputs</dt>
                      <dd className={styles['mono']}>
                        {predicate.inputs.map((input) => `${input.name}: ${input.type}`).join(', ')}
                      </dd>
                    </div>
                  )}
                </dl>
              </article>
            ))}
          </section>
        )
      })}

      {entries.length === 0 && (
        <p className={styles['empty']}>
          This workspace’s policy pack declares no rules. That is the pack’s answer, not a failure
          to load it.
        </p>
      )}

      <HairlineRule />

      <section className={styles['category']}>
        <h2 className={styles['categoryTitle']}>What the policy can see</h2>
        <p className={styles['lede']}>
          The fact types a rule may be written against. A field absent here cannot be reasoned
          about, which is why the list is worth showing rather than assuming.
        </p>
        <dl className={styles['meta']}>
          {Object.entries(factTypes).map(([name, factType]) => (
            <div key={name}>
              <dt className={styles['mono']}>{name}</dt>
              <dd>
                {factType.description}
                <div className={styles['mono']}>{Object.keys(factType.fields).join(', ')}</div>
              </dd>
            </div>
          ))}
        </dl>
        {completeness.isSubset && <p className={styles['caveat']}>{completeness.warning}</p>}
      </section>

      {Object.keys(knownLimitations).length > 0 && (
        <>
          <HairlineRule />
          <section className={styles['category']}>
            <h2 className={styles['categoryTitle']}>What it deliberately cannot answer</h2>
            <p className={styles['lede']}>
              Published by the platform, and interpolated verbatim into the Ask surface’s prompt —
              so a new pack changes what the model will claim, with no code change here.
            </p>
            <dl className={styles['meta']}>
              {Object.entries(knownLimitations).map(([key, explanation]) => (
                <div key={key}>
                  <dt className={styles['mono']}>{key}</dt>
                  <dd>{explanation}</dd>
                </div>
              ))}
            </dl>
          </section>
        </>
      )}
    </main>
  )
}
